/**
 * Copyright (c) 2022 Yoshinobu Obata
 * Released under the Apache-2.0 license
 * LICENSE: http://www.apache.org/licenses/LICENSE-2.0
 * 
 * 感情分析拡張機能用の翻訳モジュール
 * 翻訳拡張機能が読み込まれていない場合のフォールバックとして使用
 */
const log = require('../../util/log');
const { LanguageCodes } = require('./config');
const { detectLanguage, waitForModelReady } = require('./utils');
const { setSharedModule } = require('./shared');

// 共有モジュール名（翻訳拡張機能と同じ名前を使用）
const TRANSLATION_MODULE_NAME = 'translationModule';

class TranslationModule {
    constructor() {
        this._translator = null;
        this._status = 'not loaded';
        this._translationCache = new Map();
        
        // 自身を共有モジュールとして登録
        setSharedModule(TRANSLATION_MODULE_NAME, this);
    }

    getStatus() {
        return this._status;
    }

    async initialize() {
        if (this._translator || this._status === 'loading') {
            await waitForModelReady(() => this._status);
            return this._translator;
        }
        
        this._status = 'loading';
        try {
            const { pipeline } = window.transformers;
            
            log.log('Loading translation model (from emotion extension)...');
            this._translator = await pipeline(
                'translation',
                'Xenova/nllb-200-distilled-600M',
                {
                    dtype: 'q8',
                    device: 'wasm'
                }
            );
            
            this._status = 'ready';
            log.log('Translation model loaded successfully');
            alert("翻訳モデルの読み込みが完了しました。");
            return this._translator;
        } catch (error) {
            this._status = 'error';
            log.error('Failed to initialize translation model:', error);
            throw error;
        }
    }

    async translate(text, targetLanguage) {
        const cacheKey = `${text}:${targetLanguage}`;
        
        if (this._translationCache.has(cacheKey)) {
            return this._translationCache.get(cacheKey);
        }

        try {
            await this.initialize();
            
            // ターゲット言語コードを取得
            const tgtLangCode = LanguageCodes[targetLanguage];
            if (!tgtLangCode) {
                log.error('Unsupported target language:', targetLanguage);
                return text;
            }

            // 入力テキストの言語を検出
            const detectedLang = detectLanguage(text);
            const srcLangCode = LanguageCodes[detectedLang] || 'eng_Latn';
            
            // 同じ言語への翻訳は不要
            if (detectedLang === targetLanguage) {
                log.log(`Same language detected (${detectedLang}), returning original text`);
                return text;
            }
            
            log.log(`Translating from ${detectedLang} (${srcLangCode}) to ${targetLanguage} (${tgtLangCode})`);

            const result = await this._translator(text, {
                src_lang: srcLangCode,
                tgt_lang: tgtLangCode
            });
            
            const translatedText = result[0].translation_text;
            
            // キャッシュサイズ制限
            if (this._translationCache.size > 100) {
                const firstKey = this._translationCache.keys().next().value;
                this._translationCache.delete(firstKey);
            }
            this._translationCache.set(cacheKey, translatedText);
            
            log.log(`Translated (${detectedLang} -> ${targetLanguage}): "${text}" -> "${translatedText}"`);
            
            return translatedText;
        } catch (error) {
            log.error('Translation error:', error);
            return text;
        }
    }

    async translateToEnglish(text) {
        return await this.translate(text, 'english');
    }
}

TranslationModule.SHARED_MODULE_NAME = TRANSLATION_MODULE_NAME;

module.exports = TranslationModule;